import { importedTemplateIdAtom } from '@/atoms/templateCommunityAtoms';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useTutorial } from '@/hooks/useTutorial';
import { sleep } from '@/lib/utils';
import { isTauri } from '@/tauri/utils';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { TemplatePages } from '@heidi/schema/ml-scribe';
import { useAtom } from 'jotai';
import { isEmpty } from 'lodash-es';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

export const useImportTemplate = () => {
  const { isAuthenticated } = useIsAuthenticated();
  const [templateIdToImport, setTemplateIdToImport] = useAtom(importedTemplateIdAtom);
  const { onNextPage, tutorialStepCurrentPage, panelOpen } = useTutorial();
  const shouldHighlightAddToLibrary =
    tutorialStepCurrentPage?.enum === TemplatePages.ADD_TO_YOUR_LIBRARY && panelOpen;
  const router = useRouter();

  const importTemplate = useCallback(
    async ({ templateId }: { templateId: string }) => {
      if (isEmpty(templateId)) {
        return;
      }
      if (templateIdToImport !== templateId) {
        setTemplateIdToImport(templateId);
      }
      await sleep(10);

      // the page can be accessed from https://heidihealth.com/templates on prod,
      // so we need to make sure we go to the scribe domain for auth and redirects
      const isOnScribeDomain = window.location.hostname === 'scribe.heidihealth.com';
      const goToScribe =
        getEnvironment() === ENV_NAME.PROD && !isTauri() && !isOnScribeDomain;

      const hash = window?.location.hash || '';
      if (isAuthenticated) {
        if (shouldHighlightAddToLibrary) {
          onNextPage();
        }
        if (goToScribe) {
          window.location.href = `https://scribe.heidihealth.com/scribe/my-templates/community-import${hash}`;
        } else {
          await router.push({
            pathname: `/scribe/my-templates/community-import`,
            hash,
          });
        }
      } else {
        if (goToScribe) {
          window.location.href = `https://scribe.heidihealth.com/api/auth/login?post_login_redirect_url=/scribe/my-templates/community-import${hash}`;
        } else {
          await router.push({
            pathname: `/api/auth/login`,
            query: {
              post_login_redirect_url: `/scribe/my-templates/community-import${hash}`,
            },
          });
        }
      }
    },
    [
      templateIdToImport,
      isAuthenticated,
      setTemplateIdToImport,
      shouldHighlightAddToLibrary,
      onNextPage,
      router,
    ]
  );

  return { importTemplate };
};
